// ════════════════════════════════════════════════════════════════════════
// RTR SCORE — MOTEUR DE CALCUL PUR
// (mesures patient) → (statut par hurdle + score composite + décision)
//
// Découplé de l'UI : aucune dépendance React, aucun accès réseau/DOM.
// Consomme uniquement src/rtrRules.js (source de vérité des seuils/poids).
// Réutilisable tel quel par une future vue patient gamifiée.
//
// ── Contrat d'entrée `measurements` ────────────────────────────────────
// Objet plat { [critereId]: valeur }. Forme de la valeur selon le critère :
//   - numérique simple (seuil numérique)           → number
//   - booléen (toggle "conforme ?")                 → true | false
//   - qualitatif épanchement (0=absent…4=3+ sévère) → number 0-4
//   - qualité squat Crossley                        → "Pauvre (Poor)" | "Passable (Fair)" | "Bon (Good)"
//   - variable symétrique (RTR-SPORT §2.1/R4)       → { abs?, epic?, lsi }
//   - RSI / temps de contact                        → { dl, sl }
//   - hurdle H9 RTR-RUN (fallback MicroFET)          → { isok } ou { microfet, epic }
//   - hurdle dérobement post-op (V1.14)             → { answer: true|false|undefined, count, since }
//     answer = réponse du jour à « Dérobement depuis le dernier bilan ? » (true = oui)
// Une valeur absente (undefined/null) = NON_MESURE — jamais interprétée comme échec.
//
// ── Option `stale` (règle de fraîcheur, 15/10/2026) ────────────────────
// { [critereId]: { maxDays, ageDays, date } } — mesure existante mais trop ancienne.
// Sa valeur n'est PAS transmise (absente de `measurements`) :
//   - critère scoré   → NON_MESURE, retiré du numérateur ET du dénominateur (R3) ;
//   - hurdle bloquant → statut A_RETESTER (≠ FAIL) : n'entraîne pas de NO_GO
//     mais empêche le GO tant que la mesure n'est pas refaite.
// ════════════════════════════════════════════════════════════════════════

import { RTR_RUN, RTR_SPORT, RTR_SPORT_COMPOSANTES } from "./rtrRules";

const EPANCH_LABELS = ["0 (absent)", "Trace", "1+ (léger)", "2+ (modéré)", "3+ (sévère)"];
const EPANCH_THRESHOLD = { "0 (absent)": 0, absent: 0, trace: 1, Trace: 1, "1+": 2, "2+": 3, "3+": 4 };
const CROSSLEY_ORDER = { "Pauvre (Poor)": 0, "Passable (Fair)": 1, "Bon (Good)": 2 };

function isEmpty(v) { return v === undefined || v === null || v === ""; }

// ── Évaluation d'un hurdle ────────────────────────────────────────────
// Retourne { status: "PASS"|"FAIL"|"NON_MESURE"|"A_RETESTER", detail, capStatus? }
function evaluateHurdleGeneric(hurdle, raw) {
  if (hurdle.direction === "zero_giving_way") return evaluateGivingWay(raw);
  if (hurdle.id === "quad_lsi" && hurdle.fallback) {
    if (!raw) return { status: "NON_MESURE", detail: "Aucune mesure fournie" };
    if (!isEmpty(raw.isok)) {
      return raw.isok >= hurdle.threshold
        ? { status: "PASS", detail: `LSI isocinétique ${raw.isok}% (cible réelle 80%)` }
        : { status: "FAIL", detail: `LSI isocinétique ${raw.isok}% < 70%` };
    }
    if (!isEmpty(raw.microfet) && !isEmpty(raw.epic)) {
      const pass = raw.microfet >= 70 && raw.epic >= 70;
      return pass
        ? { status: "PASS", detail: `Fallback MicroFET ${raw.microfet}% + %EPIC ${raw.epic}%`, capStatus: "GO_CONDITIONNEL" }
        : { status: "FAIL", detail: `Fallback MicroFET ${raw.microfet}% / %EPIC ${raw.epic}% — sous 70%` };
    }
    return { status: "NON_MESURE", detail: "Ni isocinétique ni fallback MicroFET renseignés" };
  }

  if (isEmpty(raw)) return { status: "NON_MESURE", detail: "Non mesuré" };

  switch (hurdle.direction) {
    case "gte": return { status: raw >= hurdle.threshold ? "PASS" : "FAIL", detail: `${raw}${hurdle.unit || ""} vs ≥ ${hurdle.threshold}${hurdle.unit || ""}` };
    case "gt": return { status: raw > hurdle.threshold ? "PASS" : "FAIL", detail: `${raw}${hurdle.unit || ""} vs > ${hurdle.threshold}${hurdle.unit || ""}` };
    case "lte": return { status: raw <= hurdle.threshold ? "PASS" : "FAIL", detail: `${raw}${hurdle.unit || ""} vs ≤ ${hurdle.threshold}${hurdle.unit || ""}` };
    case "eq": {
      if (typeof hurdle.threshold === "number") return { status: raw === hurdle.threshold ? "PASS" : "FAIL", detail: `${raw} vs = ${hurdle.threshold}` };
      // seuil descriptif (ex. "complète (0°)") → mesure fournie en booléen "conforme ?"
      return { status: raw ? "PASS" : "FAIL", detail: raw ? `Conforme (${hurdle.threshold})` : `Non conforme (${hurdle.threshold})` };
    }
    case "eq_bool": return { status: !!raw === !!hurdle.threshold ? "PASS" : "FAIL", detail: raw ? "Oui" : "Non" };
    case "lte_qualitative": {
      const val = typeof raw === "number" ? raw : EPANCH_THRESHOLD[raw];
      const th = EPANCH_THRESHOLD[hurdle.threshold] ?? 1;
      if (isEmpty(val)) return { status: "NON_MESURE", detail: "Non mesuré" };
      return { status: val <= th ? "PASS" : "FAIL", detail: `${EPANCH_LABELS[val] || val} vs ≤ ${hurdle.threshold}` };
    }
    case "gte_categorical": {
      const order = CROSSLEY_ORDER[raw];
      if (isEmpty(order)) return { status: "NON_MESURE", detail: "Non mesuré" };
      return { status: order >= 2 ? "PASS" : "FAIL", detail: `${raw} vs ≥ Bon` };
    }
    case "gte_baseline": return { status: raw ? "PASS" : "FAIL", detail: raw ? "Atteint" : "Non atteint" };
    default: return { status: "NON_MESURE", detail: "Direction non gérée" };
  }
}

// Dérobement post-op (V1.14) : 0 toléré. Échec si « oui » au bilan OU ≥ 1 événement
// daté dans la fenêtre ; sans réponse du jour → À RETESTER (jamais PASS, même à 0 événement).
function evaluateGivingWay(raw = {}) {
  const count = raw?.count ?? 0;
  const since = raw?.since ? ` depuis le ${raw.since.split("-").reverse().join("/")}` : "";
  if (raw?.answer === true) return { status: "FAIL", detail: `Dérobement déclaré au bilan${count ? ` (${count} événement(s) daté(s)${since})` : " — dater l'épisode"}` };
  if (count > 0) return { status: "FAIL", detail: `${count} dérobement(s)${since}` };
  if (raw?.answer !== false) return { status: "A_RETESTER", detail: "Question obligatoire « Dérobement depuis le dernier bilan ? » sans réponse ce jour — jamais PASS par défaut" };
  return { status: "PASS", detail: `Aucun dérobement${since} (réponse « non » au bilan)` };
}

function staleDetail(st) {
  const when = st.date ? ` le ${st.date.split("-").reverse().join("/")}` : "";
  return `Mesure périmée (mesurée${when}, ${st.ageDays} j > ${st.maxDays} j) — à refaire`;
}

function evaluateHurdles(hurdles, measurements, stale = {}) {
  return hurdles.map(h => {
    const r = evaluateHurdleGeneric(h, measurements[h.id]);
    // Une mesure fraîche (même partielle, ex. fallback H9) prime ; sinon périmée → À RETESTER
    if (r.status === "NON_MESURE" && stale[h.id]) return { ...h, status: "A_RETESTER", detail: staleDetail(stale[h.id]), stale: stale[h.id] };
    return { ...h, ...r };
  });
}

// ── Fraction d'atteinte 0→1 pour un critère scoré (RTR-RUN) ────────────
function criterionFraction(criterion, raw) {
  if (isEmpty(raw)) return null; // NON_MESURE → exclu num/dénom (R3)
  const s = criterion.scale;
  switch (s.mode) {
    // V1.15 (D2) : seuil (min) = 50 %, max = 100 % ; sous le seuil, crédit proportionnel
    case "linear_range": return raw < s.min ? clamp01((raw / s.min) * 0.5) : clamp01(0.5 + 0.5 * (raw - s.min) / (s.max - s.min));
    case "linear_range_inverse": return clamp01((s.max - raw) / (s.max - s.min));
    case "ratio_to_threshold": return clamp01(raw / s.threshold);
    case "direct_percent": return clamp01(raw / 100);
    case "boolean": return raw ? 1 : 0;
    case "categorical": return s.map[raw] ?? 0;
    default: return null;
  }
}

function clamp01(x) { return Math.max(0, Math.min(1, x)); }

// ── RTR-RUN ─────────────────────────────────────────────────────────
export function computeRtrRun(measurements = {}, { stale = {} } = {}) {
  const hurdles = evaluateHurdles(RTR_RUN.hurdles, measurements, stale);
  const decision = deriveDecisionFromHurdles(hurdles, RTR_RUN.decision, RTR_RUN.hurdles);

  const blocks = RTR_RUN.blocks.map(block => {
    let earned = 0, possible = 0;
    const criteria = block.criteria.map(c => {
      const raw = measurements[c.id];
      const frac = criterionFraction(c, raw);
      if (frac !== null) { earned += c.weight * frac; possible += c.weight; }
      return { ...c, value: raw, fraction: frac, nonMesure: frac === null, stale: frac === null ? stale[c.id] || null : null };
    });
    const pct = possible > 0 ? (earned / possible) * 100 : null;
    return { ...block, criteria, earned, possible, pct };
  });

  const scoreInfo = totalScore(blocks);

  return {
    gate: "RTR-RUN",
    profile: null,
    hurdles,
    blocks,
    score: scoreInfo.score,
    scoreRenormalized: scoreInfo.renormalized,
    scoreCoverage: scoreInfo.coverage,
    decision: decision.status,
    decisionSource: decision.source,
    action: decision.action,
    justification: buildJustification("RTR-RUN", null, decision, hurdles, scoreInfo.score),
  };
}

// ── RTR-SPORT ───────────────────────────────────────────────────────
export function computeRtrSport(profile, measurements = {}, { stale = {} } = {}) {
  const allHurdles = [...RTR_SPORT.hurdlesCommon, ...(RTR_SPORT.hurdlesByProfile[profile] || [])];
  const hurdles = evaluateHurdles(allHurdles, measurements, stale);
  const decisionTable = RTR_SPORT.decisionByProfile[profile];
  const decision = deriveDecisionFromHurdles(hurdles, decisionTable, allHurdles);

  const blocksDef = RTR_SPORT.blocksByProfile[profile];
  const composanteResults = computeSportComposantes(profile, measurements, stale);

  const blocks = blocksDef.map(block => {
    if (block.weight === 0) return { ...block, earned: 0, possible: 0, pct: null };
    if (block.autoFullIfHurdlesPass) {
      // Bloc clinique : ses items (épanchement, ROM) sont déjà couverts par
      // les hurdles bloquants communs H3/H4. Si on calcule un score, c'est que
      // R1 a déjà validé tous les hurdles (sinon NO_GO, score non calculé) —
      // le bloc est donc mécaniquement à 100%. Cf. §5.1 R1.
      return { ...block, earned: block.weight, possible: block.weight, pct: 100 };
    }
    if (block.split) {
      let earned = 0, possible = 0;
      const parts = Object.entries(block.split).map(([composanteId, w]) => {
        const c = composanteResults[composanteId];
        if (!c || c.applicable === false) return { composanteId, weight: w, applicable: false };
        if (c.fraction !== null) { earned += w * c.fraction; possible += w; }
        return { composanteId, weight: w, fraction: c.fraction, nonMesure: c.fraction === null };
      });
      return { ...block, parts, earned, possible, pct: possible > 0 ? (earned / possible) * 100 : null };
    }
    // Bloc réparti également entre les composantes qu'il couvre
    const entries = block.composantes.flatMap(cid => composanteResults[cid]?.entries || []);
    const applicableEntries = entries.filter(e => e.applicable !== false);
    const perEntryWeight = applicableEntries.length ? block.weight / applicableEntries.length : 0;
    let earned = 0, possible = 0;
    const criteria = applicableEntries.map(e => {
      if (e.fraction !== null) { earned += perEntryWeight * e.fraction; possible += perEntryWeight; }
      return { ...e, weight: perEntryWeight };
    });
    return { ...block, criteria, earned, possible, pct: possible > 0 ? (earned / possible) * 100 : null };
  });

  const scoreInfo = totalScore(blocks);

  return {
    gate: "RTR-SPORT",
    profile,
    hurdles,
    blocks,
    composantes: composanteResults,
    score: scoreInfo.score,
    scoreRenormalized: scoreInfo.renormalized,
    scoreCoverage: scoreInfo.coverage,
    decision: decision.status,
    decisionSource: decision.source,
    action: decision.action,
    justification: buildJustification("RTR-SPORT", profile, decision, hurdles, scoreInfo.score),
  };
}

// Calcule, pour chaque composante affichée, la liste des entrées de seuil
// (SPORT_THRESHOLDS) qui lui appartiennent pour ce profil, avec leur fraction 0→1.
function computeSportComposantes(profile, measurements, stale = {}) {
  const out = {};
  for (const comp of RTR_SPORT_COMPOSANTES) out[comp.id] = { entries: [], fraction: null, applicable: true };

  for (const [key, entry] of Object.entries(RTR_SPORT.thresholds)) {
    if (entry.excellenceOnly) continue; // jamais dans le score (informatif seul)
    const target = entry[profile];
    const applicable = target !== "non exigé";
    const bucket = out[entry.composante];
    if (!bucket) continue;

    if (!applicable) {
      bucket.entries.push({ id: key, label: entry.label, applicable: false });
      continue;
    }
    if (entry.nonMesure) {
      bucket.entries.push({ id: key, label: entry.label, applicable: true, fraction: null, nonMesure: true, note: entry.note });
      continue;
    }

    const raw = measurements[key];
    let fraction = null, displayValue = raw;

    if (key === "rsi" || key === "contact_time") {
      const s = entry.structured[profile];
      if (raw && !isEmpty(raw.dl) && !isEmpty(raw.sl)) {
        const fDl = s.direction === "gte" ? clamp01(raw.dl / s.dl) : clamp01(s.dl / raw.dl);
        const fSl = s.direction === "gte" ? clamp01(raw.sl / s.sl) : clamp01(s.sl / raw.sl);
        fraction = (fDl + fSl) / 2;
      }
    } else if (typeof target === "number") {
      // variable symétrique : abs OU %EPIC, comparé au seuil absolu du profil (R4)
      if (raw && (!isEmpty(raw.abs) || !isEmpty(raw.epic))) {
        const best = Math.max(raw.abs ?? -Infinity, raw.epic ?? -Infinity);
        fraction = clamp01(best / target);
      }
      displayValue = raw;
    } else {
      // cible qualitative (baseline/moyenne du sport, "exigé", contrôle moteur…)
      // mesurée par le praticien comme conforme/non conforme
      if (!isEmpty(raw)) fraction = raw?.meets ? 1 : (raw === true ? 1 : raw === false ? 0 : null);
      displayValue = raw;
    }

    bucket.entries.push({ id: key, label: entry.label, applicable: true, fraction, value: displayValue, tier: entry.tier, unit: entry.unit, stale: fraction === null ? stale[key] || null : null });
  }

  for (const comp of RTR_SPORT_COMPOSANTES) {
    const applicableEntries = out[comp.id].entries.filter(e => e.applicable !== false);
    const measured = applicableEntries.filter(e => e.fraction !== null && e.fraction !== undefined);
    out[comp.id].fraction = measured.length ? measured.reduce((s, e) => s + e.fraction, 0) / measured.length : null;
    out[comp.id].applicable = applicableEntries.length > 0;
  }
  return out;
}

// coverage = part des points du score réellement mesurés (R3 : un critère non mesuré est
// retiré du numérateur ET du dénominateur — un score sur 40 % des points se lit avec prudence).
function totalScore(blocks) {
  let earned = 0, possible = 0;
  for (const b of blocks) { earned += b.earned || 0; possible += b.possible || 0; }
  const max = blocks.reduce((s, b) => s + (b.weight || 0), 0);
  const score = possible > 0 ? Math.round((earned / possible) * 100) : null;
  return { score, renormalized: possible < max, coverage: max > 0 ? Math.round((possible / max) * 100) : null };
}

// ── Décision — R1 exclusivement (hurdles), R2 neutralisée ─────────────
function deriveDecisionFromHurdles(hurdles, decisionTable, hurdleDefs) {
  const failed = hurdles.filter(h => h.status === "FAIL");
  const nonMesure = hurdles.filter(h => h.status === "NON_MESURE");
  const retest = hurdles.filter(h => h.status === "A_RETESTER");
  const cappedByFallback = hurdles.find(h => h.capStatus);

  if (failed.length > 0) {
    const fallbackAction = "Maintien de la phase actuelle — corriger le(s) hurdle(s) en échec avant nouvelle évaluation.";
    return { status: "NO_GO", source: "hurdle", failed, action: decisionTable.find(d => d.status === "NO_GO")?.action || fallbackAction };
  }
  if (nonMesure.length > 0) {
    return { status: "NO_GO", source: "donnee_manquante", nonMesure, action: "Hurdle(s) bloquant(s) non mesuré(s) — impossible de certifier le passage. Compléter le bilan avant nouvelle évaluation." };
  }
  if (retest.length > 0) {
    // Mesure périmée : ni NO_GO ni GO — la décision est suspendue jusqu'au re-test
    return { status: "A_RETESTER", source: "mesure_perimee", retest, action: `Compléter avant décision : ${retest.map(h => h.label).join(", ")}.` };
  }
  // Tous les hurdles passent : le score /100 devient calculable, mais NE PARTICIPE PAS
  // à cette décision (R2 neutralisée) — sauf plafond explicite d'un fallback documenté (H9 RTR-RUN).
  const capped = cappedByFallback ? "GO_CONDITIONNEL" : null;
  const best = decisionTable.find(d => d.status === "GO");
  const status = capped === "GO_CONDITIONNEL" ? "GO_CONDITIONNEL" : (best?.status || "GO");
  return { status, source: capped ? "hurdle_fallback" : "hurdles_ok", action: decisionTable.find(d => d.status === status)?.action };
}

function buildJustification(gate, profile, decision, hurdles, score) {
  const failed = hurdles.filter(h => h.status === "FAIL");
  const nonMesure = hurdles.filter(h => h.status === "NON_MESURE");
  const retest = hurdles.filter(h => h.status === "A_RETESTER");
  const profileTxt = profile ? ` (profil ${profile})` : "";
  if (decision.status === "NO_GO" && failed.length) {
    return `NO_GO${profileTxt} — ${failed.length} hurdle(s) bloquant(s) en échec : ${failed.map(h => h.label).join(", ")}. Décision basée exclusivement sur les hurdles individuels (R1) — le score de suivi (${score ?? "n/a"}/100) n'entre pas en compte.`;
  }
  if (decision.status === "NO_GO" && nonMesure.length) {
    return `NO_GO${profileTxt} — ${nonMesure.length} hurdle(s) bloquant(s) non mesuré(s) : ${nonMesure.map(h => h.label).join(", ")}. Compléter le bilan avant de pouvoir statuer.`;
  }
  if (decision.status === "A_RETESTER") {
    return `À RETESTER${profileTxt} — aucun hurdle en échec, mais ${retest.length} hurdle(s) bloquant(s) à compléter (mesure périmée ou question obligatoire sans réponse) : ${retest.map(h => h.label).join(", ")}. Ce n'est pas un NO_GO : le GO reste impossible tant que ce n'est pas fait.`;
  }
  if (decision.status === "GO_CONDITIONNEL" && decision.source === "hurdle_fallback") {
    return `GO_CONDITIONNEL${profileTxt} — tous les hurdles passent, mais via un fallback documenté (mesure isocinétique indisponible, MicroFET + %EPIC utilisés) qui plafonne le statut par prudence. Score de suivi : ${score ?? "n/a"}/100.`;
  }
  return `${decision.status}${profileTxt} — tous les hurdles bloquants sont validés (R1). Le score de suivi (${score ?? "n/a"}/100) est affiché à titre indicatif uniquement (R5) et n'a pas influencé cette décision.`;
}

export default { computeRtrRun, computeRtrSport };
